/**
 * Event detail — cover placeholder, meta, roster, RSVP segmented (Going /
 * Maybe / Can't go) with optimistic counts. Wired: GET /api/events/:id +
 * POST /api/events/:id/rsvp. Port of EventDetail in
 * design/source/screens-poll-event.jsx.
 */
import { useLocalSearchParams } from 'expo-router';
import React, { useState } from 'react';
import { Share, View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { Icon } from '@/components/gate/icons';
import { ArchAvatar, BottomBar, CardSkeletons, CatPill, goBack, PhotoPlaceholder, Segmented, WebCap } from '@/features/community/bits';
import { eventCatLabel, fetchEvent, rsvpEvent, useFetch, withRsvp, type Rsvp } from '@/features/community/lib';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, EmptyState, IconButton, Screen, TopBar } from '@/ui';

export default function EventDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t } = useT();
  const { colors } = useTheme();
  const RSVP_OPTIONS = [
    { key: 'GOING', label: t('event_rsvp_going') },
    { key: 'MAYBE', label: t('event_rsvp_maybe') },
    { key: 'NO', label: t('event_rsvp_cant') },
  ];
  const { data: event, loading, error, refetch, mutate } = useFetch(() => fetchEvent(String(id)), [id]);
  const [busy, setBusy] = useState(false);
  const [rsvpErr, setRsvpErr] = useState<string | null>(null);

  const setRsvp = async (status: Rsvp) => {
    if (!event || busy) return;
    const prev = event;
    setBusy(true);
    setRsvpErr(null);
    mutate(() => withRsvp(prev, status));
    try {
      await rsvpEvent(prev.id, status);
    } catch {
      mutate(() => prev);
      setRsvpErr(t('event_rsvp_error'));
    } finally {
      setBusy(false);
    }
  };

  const share = () => {
    if (!event) return;
    void Share.share({ message: `${event.title} — ${event.when}${event.venue ? ` at ${event.venue}` : ''}` }).catch(() => {});
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <TopBar
        title={t('event_detail_title')}
        onBack={goBack}
        right={event ? (
          <IconButton label="Share event" onPress={share}>
            <Icon name="share" size={20} color={colors.textPrimary} />
          </IconButton>
        ) : undefined}
      />
      {loading ? (
        <Screen padded style={{ paddingTop: space.s4 }}>
          <CardSkeletons count={2} />
        </Screen>
      ) : error || !event ? (
        <Screen padded>
          <EmptyState
            icon={<Icon name="calendar" size={40} color={colors.textTertiary} />}
            title={t('event_unavailable_title')}
            body={t('event_unavailable_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        </Screen>
      ) : (
        <>
          <Screen>
            <WebCap>
            <PhotoPlaceholder h={160} label="event cover image" style={{ marginHorizontal: space.s4 }} />
            <View style={{ paddingHorizontal: space.s4, paddingTop: space.s4 }}>
              <CatPill label={eventCatLabel(event.category, t)} />
              <AppText variant="headingLg" style={{ marginTop: space.s3 }}>{event.title}</AppText>

              <View style={{ backgroundColor: colors.canvas1, borderRadius: radius.lg, padding: space.s4, marginTop: space.s4, gap: space.s3 }}>
                {event.when ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                    <Icon name="calendar" size={18} color={colors.textSecondary} />
                    <AppText variant="bodyMd" style={{ flex: 1 }}>{event.when}</AppText>
                  </View>
                ) : null}
                {event.venue ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                    <Icon name="map-pin" size={18} color={colors.textSecondary} />
                    <AppText variant="bodyMd" style={{ flex: 1 }}>{event.venue}</AppText>
                  </View>
                ) : null}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                  <Icon name="users" size={18} color={colors.textSecondary} />
                  <AppText variant="bodyMd" style={{ flex: 1 }}>{t('event_going_residents', { n: event.going })}</AppText>
                </View>
              </View>

              {event.description ? (
                <AppText variant="bodyLg" style={{ marginTop: space.s5 }}>{event.description}</AppText>
              ) : null}

              {event.roster.length > 0 ? (
                <>
                  <AppText variant="label" tone="tertiary" style={{ marginTop: space.s6, marginBottom: 10 }}>{t('event_whos_going')}</AppText>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    {event.roster.slice(0, 6).map((name, i) => (
                      <View key={i} style={{ marginLeft: i ? -8 : 0 }}>
                        <ArchAvatar name={name} size={36} tone={i === 2 ? 'accent' : 'primary'} />
                      </View>
                    ))}
                    {event.going > event.roster.length ? (
                      <AppText variant="bodySm" tone="tertiary" style={{ marginLeft: space.s3 }}>
                        {t('event_more', { n: event.going - event.roster.length })}
                      </AppText>
                    ) : null}
                  </View>
                </>
              ) : null}

              {rsvpErr ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s4 }}>{rsvpErr}</AppText> : null}
            </View>
            </WebCap>
          </Screen>

          {!event.past ? (
            <BottomBar>
              <Segmented
                options={RSVP_OPTIONS}
                value={event.myRsvp}
                onChange={(k) => void setRsvp(k as Rsvp)}
                emphasis
                grow
              />
            </BottomBar>
          ) : null}
        </>
      )}
    </View>
  );
}
